"use client";
import { Modal, Result, Tag, Space, Button, List, Typography } from '@basic-components';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

interface ImportResultModalProps {
  visible: boolean;
  result: ImportResultData | null;
  onClose: () => void;
  onViewList?: () => void;
}

interface ImportResultData {
  successCount: number;
  failCount: number;
  templateName: string;
  aiAnalysisUsed: boolean;
  errorDetails?: string[];
}

const { Text } = Typography;

export const ImportResultModal = ({
  visible,
  result,
  onClose,
  onViewList,
}: ImportResultModalProps) => {
  if (!result) return null;

  const hasErrors = result.failCount > 0;
  const hasSuccess = result.successCount > 0;

  return (
    <Modal
      title="导入结果"
      open={visible}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>关闭</Button>
          {onViewList && (
            <Button type="primary" onClick={onViewList}>
              查看列表
            </Button>
          )}
        </Space>
      }
      width={600}
    >
      {/* 成功/失败状态展示 */}
      <Result
        status={hasErrors ? (hasSuccess ? 'warning' : 'error') : 'success'}
        title={
          hasErrors
            ? `导入完成，${result.failCount} 条数据处理失败`
            : '导入成功'
        }
        subTitle={
          <Space direction="vertical" size={4}>
            <div>
              <Text>模板类型：{result.templateName}</Text>
            </div>
            <div>
              <Space size="large">
                {hasSuccess && (
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    成功 {result.successCount} 条
                  </Tag>
                )}
                {hasErrors && (
                  <Tag icon={<CloseCircleOutlined />} color="error">
                    失败 {result.failCount} 条
                  </Tag>
                )}
              </Space>
            </div>
          </Space>
        }
      />

      {/* 错误详情列表 */}
      {hasErrors && (result.errorDetails?.length ?? 0) > 0 && (
        <div style={{ marginTop: 24 }}>
          <Text strong type="danger">
            失败原因：
          </Text>
          <List
            size="small"
            bordered
            dataSource={result.errorDetails}
            style={{
              marginTop: 12,
              maxHeight: 300,
              overflow: 'auto',
              background: '#fff5f5',
            }}
            renderItem={(item: string, index: number) => (
              <List.Item
                style={{ padding: '8px 12px' }}
                extra={
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                }
              >
                <Text type="danger">{item}</Text>
              </List.Item>
            )}
          />
        </div>
      )}

      {/* 提示信息 */}
      <div
        style={{
          marginTop: 24,
          padding: 16,
          background: '#f5f5f5',
          borderRadius: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hasErrors
            ? '提示：请根据错误原因修改 Excel 文件后重新导入。部分成功的数据已经保存。'
            : '导入完成，所有数据已成功保存。'}
        </Text>
      </div>
    </Modal>
  );
};

export default ImportResultModal;
